import { Injectable, isDevMode } from '@angular/core';

/**
 * Metrics for token refresh operations
 */
export interface TokenRefreshMetrics {
  attempts: number;
  successes: number;
  failures: number;
  retries: number;
  averageRefreshTime: number;
  lastRefreshTime: Date | null;
  lastFailureTime: Date | null;
  lastFailureReason: string | null;
}

/**
 * Service to track token refresh metrics for monitoring and debugging.
 */
@Injectable({
  providedIn: 'root',
})
export class TokenRefreshMetricsService {
  private metrics: TokenRefreshMetrics = {
    attempts: 0,
    successes: 0,
    failures: 0,
    retries: 0,
    averageRefreshTime: 0,
    lastRefreshTime: null,
    lastFailureTime: null,
    lastFailureReason: null,
  };

  private refreshTimes: number[] = []; // Store last 10 refresh times for average calculation
  private readonly MAX_REFRESH_TIMES = 10;

  /**
   * Get current metrics
   */
  getMetrics(): TokenRefreshMetrics {
    return { ...this.metrics };
  }

  /**
   * Record a refresh attempt
   */
  recordAttempt(): void {
    this.metrics.attempts++;
    if (isDevMode()) {
      console.log(`[TokenRefreshMetrics] Refresh attempt #${this.metrics.attempts}`);
    }
  }

  /**
   * Record a successful refresh
   * @param duration Refresh duration in milliseconds
   */
  recordSuccess(duration: number = 0): void {
    this.metrics.successes++;
    this.metrics.lastRefreshTime = new Date();

    if (duration > 0) {
      this.refreshTimes.push(duration);
      if (this.refreshTimes.length > this.MAX_REFRESH_TIMES) {
        this.refreshTimes.shift(); // Remove oldest
      }
      this.metrics.averageRefreshTime =
        this.refreshTimes.reduce((sum, time) => sum + time, 0) / this.refreshTimes.length;
    }

    if (isDevMode()) {
      const successRate =
        this.metrics.attempts > 0
          ? ((this.metrics.successes / this.metrics.attempts) * 100).toFixed(2)
          : '0.00';
      console.log(
        `[TokenRefreshMetrics] Refresh success (${successRate}% success rate, ${this.metrics.successes}/${this.metrics.attempts} attempts)`
      );
    }
  }

  /**
   * Record a failed refresh
   * @param reason Failure reason/error message
   */
  recordFailure(reason: string = 'Unknown error'): void {
    this.metrics.failures++;
    this.metrics.lastFailureTime = new Date();
    this.metrics.lastFailureReason = reason;

    if (isDevMode()) {
      const failureRate =
        this.metrics.attempts > 0
          ? ((this.metrics.failures / this.metrics.attempts) * 100).toFixed(2)
          : '0.00';
      console.error(
        `[TokenRefreshMetrics] Refresh failure (${failureRate}% failure rate, ${this.metrics.failures}/${this.metrics.attempts} attempts): ${reason}`
      );
    }
  }

  /**
   * Record a retry attempt
   */
  recordRetry(attemptNumber: number): void {
    this.metrics.retries++;
    if (isDevMode()) {
      console.log(`[TokenRefreshMetrics] Retry attempt #${attemptNumber}`);
    }
  }

  /**
   * Reset all metrics (useful for testing or logout)
   */
  reset(): void {
    this.metrics = {
      attempts: 0,
      successes: 0,
      failures: 0,
      retries: 0,
      averageRefreshTime: 0,
      lastRefreshTime: null,
      lastFailureTime: null,
      lastFailureReason: null,
    };
    this.refreshTimes = [];
    if (isDevMode()) {
      console.log('[TokenRefreshMetrics] Metrics reset');
    }
  }

  /**
   * Get success rate as percentage
   */
  getSuccessRate(): number {
    if (this.metrics.attempts === 0) {
      return 0;
    }
    return (this.metrics.successes / this.metrics.attempts) * 100;
  }

  /**
   * Get failure rate as percentage
   */
  getFailureRate(): number {
    if (this.metrics.attempts === 0) {
      return 0;
    }
    return (this.metrics.failures / this.metrics.attempts) * 100;
  }
}
